import { useCallback, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { cn } from "@/lib/utils";

export function UploadDropzone({
  accept = "image/*",
  multiple = true,
  onFiles,
  className,
  hint = "Drop photos here or click to browse",
}: {
  accept?: string;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
  className?: string;
  hint?: React.ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const acceptFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const arr: File[] = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        if (f) arr.push(f);
      }
      if (arr.length) onFiles(arr);
    },
    [onFiles],
  );

  return (
    <label
      htmlFor="lovd-upload"
      onDragEnter={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        acceptFiles(e.dataTransfer.files);
      }}
      className={cn(
        "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/30 p-10 text-center transition-colors hover:bg-muted/50",
        dragging && "border-primary bg-muted",
        className,
      )}
    >
      <Upload className="h-5 w-5 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">{hint}</p>
      <input
        id="lovd-upload"
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="sr-only"
        onChange={(e) => {
          acceptFiles(e.target.files);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
    </label>
  );
}
